import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router';
import { ITEM_CONDITION_LABELS, itemLabel, itemTitle, type PublicItemResponse } from '@sellit/shared';
import { NotFound } from '../App';
import { api, ApiError } from '../lib/api';
import { setPageTitle, STATUS_LABEL } from '../lib/format';
import { Icon } from '../lib/Icon';
import { PoweredBy, PreviewNotice } from '../lib/Brand';
import { InquiryForm } from './InquiryForm';

export function ItemPage() {
  const { slug = '', number = '' } = useParams();
  const [data, setData] = useState<PublicItemResponse | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [index, setIndex] = useState(0);
  const gallery = useRef<HTMLDivElement>(null);

  useEffect(() => {
    api.publicItem(slug, number).then(
      (d) => {
        setData(d);
        setPageTitle(`${itemLabel(d.item)} · ${d.sale.title}`);
      },
      setError,
    );
  }, [slug, number]);

  if (error?.status === 404) return <NotFound message="This item isn't listed anymore." />;
  if (error) return <main className="page"><p className="error">{error.message}</p></main>;
  if (!data) return <main className="page center muted">Loading…</main>;

  const { sale, item } = data;
  const onScroll = () => {
    const el = gallery.current;
    if (el) setIndex(Math.round(el.scrollLeft / el.clientWidth));
  };

  return (
    <main className="page narrow item-page">
      <Link to={`/${sale.slug}`} className="back"><Icon name="back" size={18} /> {sale.title}</Link>
      {sale.preview && <PreviewNotice status={sale.preview} />}

      <div className="gallery-wrap">
        <div className="gallery" ref={gallery} onScroll={onScroll}>
          {item.images.map((img, i) => (
            <img key={img.id} src={img.largeUrl} alt={`${itemTitle(item)}, photo ${i + 1}`} width={img.width} height={img.height} loading={i === 0 ? 'eager' : 'lazy'} />
          ))}
        </div>
        {item.status !== 'AVAILABLE' && <span className={`ribbon big ${item.status.toLowerCase()}`}>{STATUS_LABEL[item.status]}</span>}
        {item.images.length > 1 && (
          <div className="dots" aria-label={`Photo ${index + 1} of ${item.images.length}`}>
            {item.images.map((img, i) => <span key={img.id} className={i === index ? 'on' : ''} />)}
          </div>
        )}
      </div>

      <div className="item-head">
        <p className="num">
          Item #{item.number}
          {item.condition && <span className="condition"> · {ITEM_CONDITION_LABELS[item.condition]}</span>}
        </p>
        <h1>{itemTitle(item)}</h1>
        <p className="item-price">
          <span className="price">{item.priceLabel}</span>
          {item.status !== 'AVAILABLE' && <span className={`chip ${item.status.toLowerCase()}`}>{STATUS_LABEL[item.status]}</span>}
        </p>
      </div>
      {item.description && <p className="item-desc prewrap">{item.description}</p>}

      {item.status !== 'SOLD' && sale.status === 'ACTIVE' && !sale.preview && <InquiryForm slug={sale.slug} item={item} />}

      {item.status !== 'SOLD' && sale.contactNote && (
        <div className="contact-card">
          <Icon name="message" />
          <div>
            <strong>Or contact the seller</strong>
            <p className="prewrap">{sale.contactNote}</p>
            <p className="muted small">Mention item #{item.number}.</p>
          </div>
        </div>
      )}
      <p className="muted small location"><Icon name="pin" size={16} /> {sale.publicLocation}</p>
      <p className="muted small">
        Pickup and payment are arranged directly with the seller. VenLink doesn't handle payments or delivery.
      </p>
      <PoweredBy />
    </main>
  );
}
